import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack, useTheme} from '@dropbox/dig-foundations';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {ApiError, CompanyFeedbackService} from 'client';
import {t} from 'i18next';
import {FormEvent, useRef, useState} from 'react';

import CompanyFeedbackBanner from './company-feedback-assets/BB-OSwidget-new-02.svg?react';
import styles from './CompanyFeedback.module.css';
import {Tile} from './dashboard/Tile';

type SubmissionOutcome = 'success' | 'retryable_error' | 'safe_error';

const MAX_FEEDBACK_LENGTH = 10_000;

// Match Python's `len(str)` used by the API schema rather than JavaScript's
// UTF-16 code-unit length, so an emoji counts as one character in both places.
const characterCount = (value: string) => Array.from(value).length;

const outcomeForError = (error: unknown): Exclude<SubmissionOutcome, 'success'> =>
  error instanceof ApiError && error.status >= 500 ? 'retryable_error' : 'safe_error';

export const CompanyFeedback = () => {
  const {mode} = useTheme();
  const [feedback, setFeedback] = useState('');
  const [errors, setErrors] = useState<{feedback?: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jiraUrl, setJiraUrl] = useState<string>();
  const [submissionError, setSubmissionError] = useState<string>();
  const hasStarted = useRef(false);

  const getLengthError = (value: string, max: number) =>
    characterCount(value) > max ? t('string_too_long', {max}) : undefined;
  const trimmedFeedback = feedback.trim();
  const isSubmitDisabled =
    isSubmitting ||
    !trimmedFeedback ||
    Boolean(getLengthError(trimmedFeedback, MAX_FEEDBACK_LENGTH));

  const recordStarted = (value: string) => {
    if (value.trim() && !hasStarted.current) {
      hasStarted.current = true;
      analyticsLogger().logEvent('COMPANY_FEEDBACK_STARTED');
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = {
      feedback: !trimmedFeedback
        ? t('company_feedback_feedback_required')
        : getLengthError(trimmedFeedback, MAX_FEEDBACK_LENGTH),
    };
    setErrors(nextErrors);
    if (nextErrors.feedback) return;

    analyticsLogger().logEvent('COMPANY_FEEDBACK_SUBMIT_CLICKED');
    setIsSubmitting(true);
    setSubmissionError(undefined);
    setJiraUrl(undefined);
    try {
      const result =
        await CompanyFeedbackService.submitCompanyFeedbackApiV1CompanyFeedbackSubmissionsPost({
          feedback: trimmedFeedback,
        });
      setJiraUrl(result.jira_url);
      setFeedback('');
      analyticsLogger().logEvent('COMPANY_FEEDBACK_SUBMISSION_RESULT', {outcome: 'success'});
    } catch (error) {
      const outcome = outcomeForError(error);
      setSubmissionError(
        t(
          outcome === 'retryable_error'
            ? 'company_feedback_retryable_error'
            : 'company_feedback_safe_error'
        )
      );
      analyticsLogger().logEvent('COMPANY_FEEDBACK_SUBMISSION_RESULT', {outcome});
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Tile
      tileId="companyFeedback"
      label={null}
      icon={false}
      bare
      className={[styles.tile, mode === 'dark' ? styles.dark : ''].filter(Boolean).join(' ')}
    >
      <form
        className={[atoms({height: '100%', minHeight: '0'}), styles.form].join(' ')}
        aria-label={t('company_feedback_form_label')}
        onSubmit={submit}
        noValidate
      >
        <Stack gap="0" display="flex" height="100%" className={atoms({minHeight: '0'})}>
          <CompanyFeedbackBanner
            className={styles.banner}
            role="heading"
            aria-level={2}
            aria-label={t('company_feedback_banner_label')}
          />
          <Box paddingTop="8" marginBottom="8" paddingX="4" className={styles.tagline}>
            {t('company_feedback_tagline')}
          </Box>
          <Box display="flex" flexGrow={1} minHeight="0" className={styles.inputFrame}>
            <textarea
              className={[atoms({flexGrow: 1, height: '100%', minHeight: '0'}), styles.input].join(
                ' '
              )}
              id="company-feedback-body"
              aria-label={t('company_feedback_body_label')}
              placeholder={t('company_feedback_placeholder')}
              value={feedback}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setFeedback(value);
                recordStarted(value);
                setErrors((current) => ({
                  ...current,
                  feedback: getLengthError(value.trim(), MAX_FEEDBACK_LENGTH),
                }));
              }}
              aria-invalid={Boolean(errors.feedback)}
              aria-describedby={errors.feedback ? 'company-feedback-body-error' : undefined}
            />
          </Box>
          {errors.feedback && (
            <Text
              id="company-feedback-body-error"
              className={styles.message}
              color="error"
              size="small"
              role="alert"
            >
              {errors.feedback}
            </Text>
          )}
          <Box className={styles.actions} alignItems="center" paddingTop="8">
            <Box
              display="flex"
              flexGrow={1}
              minWidth="0"
              marginRight="8"
              paddingLeft="4"
              justifyContent="flex-start"
            >
              {jiraUrl ? (
                <Text
                  className={styles.actionMessage}
                  size="small"
                  role="status"
                  aria-live="polite"
                >
                  {t('company_feedback_success_prefix')}{' '}
                  <a className={styles.successLink} href={jiraUrl}>
                    {t('company_feedback_success_link')}
                  </a>
                </Text>
              ) : submissionError ? (
                <Text className={styles.actionMessage} size="small" role="alert">
                  {submissionError}
                </Text>
              ) : (
                <Text className={styles.actionMessage} size="small">
                  {t('company_feedback_jira_disclaimer')}
                </Text>
              )}
            </Box>
            <Button
              type="submit"
              variant="primary"
              className={styles.sendButton}
              isLoading={isSubmitting}
              disabled={isSubmitDisabled}
            >
              {t('company_feedback_submit')}
            </Button>
          </Box>
        </Stack>
      </form>
    </Tile>
  );
};
